import type { ChannelType, ManagedInfo, MembershipPolicy, MemberSource, Role, User } from '../api/types'

// Managed channels and group chats (contract: "Managed channels and group chats"): an integration, such as the
// HR system, decides who is in them. The app calls them "Managed" and names the integration only in tooltips and
// admin screens. Every helper tolerates the new fields being absent, e.g. summaries from an older server or objects
// that never went through Dexie.

export const policyLabels: Record<MembershipPolicy, string> = {
  managed_plus_manual: 'Managed members plus people added manually',
  managed_only: 'Managed members only',
}

/** Channel keys: 1–100 chars of [A-Za-z0-9._:-], e.g. "venue:42". */
export const EXTERNAL_KEY_PATTERN = /^[A-Za-z0-9._:-]{1,100}$/

export function isValidExternalKey(key: string): boolean {
  return EXTERNAL_KEY_PATTERN.test(key)
}

export function managedInfo(channel: { managed?: ManagedInfo | null } | null | undefined): ManagedInfo | null {
  return channel?.managed ?? null
}

/** A managing system's actor: shown by name, never offered as a person. */
export function isIntegrationUser(user: Pick<User, 'kind'> | null | undefined): boolean {
  return user?.kind === 'integration'
}

/** A person added through the management API before they ever signed in. */
export function isPlaceholderUser(user: Pick<User, 'linked'> | null | undefined): boolean {
  return user?.linked === false
}

/** A membership only the integration can end: the conversation is managed and the integration added this member. */
export function isManagedMembership(
  channel: { managed?: ManagedInfo | null },
  member: { source?: MemberSource } | null | undefined,
): boolean {
  return Boolean(managedInfo(channel)) && member?.source === 'managed'
}

/** 'loading': the conversation is managed and my member row, which decides, has not loaded yet. */
export type LeaveAccess = 'none' | 'allowed' | 'managed' | 'loading'

/**
 * Whether I can leave a conversation I'm in. Nobody leaves a direct message or a bot conversation, and a managed member
 * can't (the integration decides). `member` is my member row: undefined while it loads, null when there is none.
 */
export function leaveAccess(
  channel: { type: ChannelType; isMember: boolean; managed?: ManagedInfo | null },
  member: { source?: MemberSource } | null | undefined,
): LeaveAccess {
  if (!channel.isMember || channel.type === 'dm' || channel.type === 'bot_dm') return 'none'
  if (managedInfo(channel) && member === undefined) return 'loading'
  return isManagedMembership(channel, member) ? 'managed' : 'allowed'
}

export interface ChannelSettingsAccess {
  /** Rename, topic and archive controls are shown. */
  canEdit: boolean
  /** Sysadmin changing a managed channel: confirm first and send PATCH ?override=true. */
  override: boolean
}

/**
 * Who may change a channel's name, topic and archive flag. `canModerate` is the existing owner/admin rule;
 * a managed channel is locked to everyone except sysadmins, whose changes are overrides.
 */
export function channelSettingsAccess(
  channel: { managed?: ManagedInfo | null },
  canModerate: boolean,
  role: Role | undefined,
): ChannelSettingsAccess {
  if (!canModerate) return { canEdit: false, override: false }
  if (!managedInfo(channel)) return { canEdit: true, override: false }
  const sysadmin = role === 'sysadmin'
  return { canEdit: sysadmin, override: sysadmin }
}

/** People can be added by hand (Add people, admin Join) unless the integration alone decides membership. */
export function allowsManualMembers(channel: { managed?: ManagedInfo | null }): boolean {
  return managedInfo(channel)?.policy !== 'managed_only'
}
